import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSupabaseData } from '../../hooks/useSupabaseData';
import { useTemplates } from '../../hooks/useTemplates';
import { useI18n } from '../../context/I18nContext';
import { useToast } from '../../context/ToastContext';
import { useLeadOptions } from '../../utils/leadOptions';
import { leadsService, profilesService, templatesService, projectsService, supabaseData } from '../../services/supabase';
import { blockDemoWrite } from '../../services/dataScope';
import { useAuth } from '../../context/AuthContext';
import { useQuota } from '../../context/QuotaContext';
import { Lead, EmailTemplate, Project } from '../../types';
import { navigateToPricing } from '../../utils/navigationUtils';
import Button from '../../components/ui/Button';
import TextArea from '../../components/ui/TextArea';
import Select from '../../components/ui/Select';
import PageHeader from '../../components/ui/PageHeader';
import { PAGE_GAP, PAGE_GRID_GAP } from '../../styles/pageLayout';
import TemplateModal from '../../components/messages/TemplateModal';
import TemplatesPicker, {
  type TemplateTypeFilter,
} from '../../components/messages/TemplatesPicker';
import VariableChips from '../../components/messages/VariableChips';
import LeadSearch from '../../components/messages/LeadSearch';
import GmailSendModal from '../../components/messages/GmailSendModal';
import ProjectAttachMenu, {
  type AttachableProjectDoc,
} from '../../components/messages/ProjectAttachMenu';
import { googleGmailService, type GoogleGmailStatus, type GmailThreadDto, gmailErrorMessageKey } from '../../services/googleGmail';
import ActionIconButton from '../../components/ui/ActionIconButton';
import Tooltip from '../../components/ui/Tooltip';
import { useCreateLeadFromSearch } from '../../hooks/useCreateLeadFromSearch';
import Card, { CardHeader, CardTitle, CardContent, CardFooter } from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
import { SHORT_VIEWPORT, useMediaQuery } from '../../hooks/useMediaQuery';
import UpgradePromptModal from '../../components/ui/UpgradePromptModal';
import { 
  Mail, 
  Grid3X3,
  Send as SendIcon,
  Copy,
  Sparkles,
  Undo2,
  ExternalLink,
  Link2,
} from 'lucide-react';
import { exportToCSV, formatDateTimeForExport } from '../../utils/exportUtils';
import { isEmailCategory } from '../../utils/categoryTranslations';
import {
  buildContactOpenUrl,
  contactOpenLabelKey,
  contactOpenPrefillsMessage,
  resolveContactChannel,
} from '../../utils/contactOutbound';
import {
  MESSAGE_VARIABLES,
  buildEmailBodies,
  insertTextAtCursor,
  personalizeMessageText,
} from '../../utils/messageVariables';
import { prepareEmailSignatureHtml } from '../../utils/emailSignatureHtml';
import { aiRewriteService } from '../../services/aiRewrite';
import InlineRewriteDiff from '../../components/messages/InlineRewriteDiff';
import {
  buildHunksFromTexts,
  countPendingHunks,
  decideAllHunks,
  resolveHunksText,
  type DiffHunk,
} from '../../utils/textDiff';
import {
  loadMessagesComposeDraft,
  saveMessagesComposeDraft,
} from '../../utils/messagesComposeDraftStorage';
import {
  loadMessagesAiSession,
  saveMessagesAiSession,
  type MessagesRewriteSuggestion,
  type MessagesRewriteUndoSnapshot,
} from '../../utils/messagesAiSessionStore';

/** API error codes for AI rewrite; map to i18n keys. */
const REWRITE_ERROR_KEYS: Record<string, string> = {
  CONTENT_TOO_LONG: 'messages.rewriteError_CONTENT_TOO_LONG',
  INSTRUCTION_TOO_LONG: 'messages.rewriteError_INSTRUCTION_TOO_LONG',
  RATE_LIMIT: 'messages.rewriteError_RATE_LIMIT',
  TIMEOUT: 'messages.rewriteError_TIMEOUT',
};

const AI_QUICK_ACTION_KEYS = [
  'fixForm',
  'professional',
  'warmer',
  'shorter',
  'moreNatural',
  'translateEn',
] as const;

const Messages: React.FC = () => {
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userPlan } = useQuota();
  const { showToast, showToastWithUndo } = useToast();
  const { requestCreateLead, leadCreateModal } = useCreateLeadFromSearch();
  const { getSectorLabel } = useLeadOptions();
  // Templates: dedicated hook (useOptimisticCRUD). Categories: useSupabaseData.
  const {
    templates,
    addTemplate,
    insertTemplate,
    updateTemplate,
    deleteTemplate,
    loading: templatesLoading,
    error: templatesError,
  } = useTemplates({
    onUpdateFailed: () => showToast(t('common.saveFailedRollback'), 'error'),
  });
  const {
    categories,
    loading: dataLoading,
    error: dataError,
  } = useSupabaseData({
    include: { leads: false, projects: false, categories: true },
  });
  const loading = templatesLoading || dataLoading;
  const error = templatesError || dataError;
  const [myName, setMyName] = useState<string>('');
  const [myCompany, setMyCompany] = useState<string>('');
  const [emailSignature, setEmailSignature] = useState('');
  const [emailSignatureHtml, setEmailSignatureHtml] = useState('');
  
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isTemplatesSheetOpen, setIsTemplatesSheetOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const appliedTemplateRef = useRef<{ id: string; subject: string; content: string } | null>(null);
  const [rewriteLoading, setRewriteLoading] = useState(false);
  const [rewriteInstruction, setRewriteInstruction] = useState('');
  const [showProRewriteModal, setShowProRewriteModal] = useState(false);
  /** Pending AI proposal shown inline in the compose text (per-hunk accept/reject). */
  const [rewriteSuggestion, setRewriteSuggestion] = useState<MessagesRewriteSuggestion | null>(null);
  const [hasPreviousForRewrite, setHasPreviousForRewrite] = useState(false);
  /** Snapshot before the last accepted AI suggestion (undo). */
  const lastBeforeRewriteRef = useRef<MessagesRewriteUndoSnapshot | null>(null);
  const [typeFilter, setTypeFilter] = useState<TemplateTypeFilter>('all');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [leadProjects, setLeadProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [composeActiveField, setComposeActiveField] = useState<'subject' | 'content'>('content');
  const [gmailStatus, setGmailStatus] = useState<GoogleGmailStatus | null>(null);
  const [linkedEmailThread, setLinkedEmailThread] = useState<GmailThreadDto | null>(null);
  const [composeRecipient, setComposeRecipient] = useState('');
  const [gmailSendLoading, setGmailSendLoading] = useState(false);
  const [showGmailSendModal, setShowGmailSendModal] = useState(false);
  const [projectDocs, setProjectDocs] = useState<AttachableProjectDoc[]>([]);
  const [projectDocsLoading, setProjectDocsLoading] = useState(false);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<string[]>([]);
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const contentInputRef = useRef<HTMLTextAreaElement>(null);
  const rewriteInputRef = useRef<HTMLTextAreaElement>(null);
  const composeSourceRef = useRef<{ subject: string; content: string }>({ subject: '', content: '' });
  /** Skip persist until draft hydrate finished (avoid wiping storage on mount). */
  const composeDraftReadyRef = useRef(false);
  /** Ignore the first persist flush right after hydrate (stale empty state). */
  const skipComposeDraftPersistRef = useRef(false);
  /** Skip persist until AI session hydrate finished. */
  const aiSessionReadyRef = useRef(false);
  const skipAiSessionPersistRef = useRef(false);
  /** Restore selected project after lead projects finish loading. */
  const desiredProjectIdRef = useRef<string | null>(null);
  /** Side-by-side layout: internal scroll. Stacked layout: page scroll + auto-grow textareas. */
  const isSplitLayout = useMediaQuery('(min-width: 900px)');
  /** Compact À : / Objet : on one line when viewport height is limited. */
  const isCompactComposeFields = useMediaQuery(SHORT_VIEWPORT);

  useEffect(() => {
    if (isSplitLayout) setIsTemplatesSheetOpen(false);
  }, [isSplitLayout]);

  const MESSAGE_TEXTAREA_MIN_PX = 128;
  const REWRITE_TEXTAREA_MIN_PX = 72;

  const resetTextareaHeight = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = '';
  };

  const syncAutoGrowTextarea = (el: HTMLTextAreaElement | null, minPx: number) => {
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${Math.max(el.scrollHeight, minPx)}px`;
  };

  const syncMessageTextareaHeight = () => {
    if (isSplitLayout) return;
    syncAutoGrowTextarea(contentInputRef.current, MESSAGE_TEXTAREA_MIN_PX);
  };

  const syncRewriteTextareaHeight = () => {
    if (isSplitLayout) return;
    syncAutoGrowTextarea(rewriteInputRef.current, REWRITE_TEXTAREA_MIN_PX);
  };

  const hasVariableToken = (text: string) => /\{[a-z0-9_]+\}/i.test(text);
  const normalizeFriendlyVariableTokens = (text: string) => {
    if (!text) return text;

    const tokenMap: Record<string, string> = {
      nomdulead: '{lead_name}',
      leadname: '{lead_name}',
      prenomdulead: '{lead_first_name}',
      leadfirstname: '{lead_first_name}',
      contactdulead: '{lead_contact}',
      emaildulead: '{lead_contact}',
      leadcontact: '{lead_contact}',
      leademail: '{lead_contact}',
      secteurdulead: '{lead_sector}',
      leadsector: '{lead_sector}',
      monnom: '{my_name}',
      myname: '{my_name}',
      maentreprise: '{my_company}',
      masociete: '{my_company}',
      mycompany: '{my_company}',
      nomduprojet: '{project_name}',
      projectname: '{project_name}',
    };

    return text.replace(/\{([^{}]+)\}/g, (full, inner: string) => {
      const key = inner
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '');
      return tokenMap[key] || full;
    });
  };

  const filteredTemplates = useMemo(() => {
    if (typeFilter === 'all') return templates;
    return templates.filter((template) => template.type === typeFilter);
  }, [templates, typeFilter]);

  // Restore compose draft (subject/body source, lead, template). URL ?lead= wins for lead.
  useEffect(() => {
    if (!user?.id) {
      composeDraftReadyRef.current = false;
      return;
    }

    let cancelled = false;
    composeDraftReadyRef.current = false;

    const urlLeadId = new URLSearchParams(window.location.search).get('lead');
    const draft = loadMessagesComposeDraft(user.id);

    const subject = draft?.subject || '';
    const content = draft?.content || '';
    composeSourceRef.current = { subject, content };
    setMessageSubject(subject);
    setMessageContent(content);
    setActiveTemplateId(draft?.activeTemplateId || null);
    desiredProjectIdRef.current = draft?.projectId || null;
    setSelectedAttachmentIds(draft?.attachmentIds || []);

    skipComposeDraftPersistRef.current = true;
    composeDraftReadyRef.current = true;

    if (!urlLeadId && draft?.leadId) {
      leadsService.getById(draft.leadId).then((lead) => {
        if (cancelled || !lead) return;
        setSelectedLead(lead);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Get lead from URL
  useEffect(() => {
    let cancelled = false;
    const urlParams = new URLSearchParams(window.location.search);
    const leadId = urlParams.get('lead');
    if (!leadId) return;

    const load = async () => {
      try {
        const lead = await leadsService.getById(leadId);
        if (cancelled) return;
        if (lead) {
          setSelectedLead(lead);
        }
      } finally {
        // Clean URL after selection (even if not found)
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load projects for optional project variables when lead changes
  useEffect(() => {
    let cancelled = false;
    const leadId = selectedLead?.id ?? null;
    setLeadProjects([]);
    setSelectedProject(null);

    if (!leadId) {
      setProjectsLoading(false);
      return;
    }

    setProjectsLoading(true);
    projectsService
      .getByLeadIds([leadId])
      .then((projects) => {
        if (cancelled) return;
        const active = projects.filter((p) => p.status !== 'ARCHIVED');
        setLeadProjects(active);
        const desiredId = desiredProjectIdRef.current;
        const match = desiredId ? active.find((p) => p.id === desiredId) || null : null;
        setSelectedProject(match);
        // Drop stale desired id if it does not belong to this lead
        if (desiredId && !match) {
          desiredProjectIdRef.current = null;
        }
      })
      .catch((err) => {
        console.error('Failed to load lead projects:', err);
        if (!cancelled) {
          setLeadProjects([]);
          setSelectedProject(null);
        }
      })
      .finally(() => {
        if (!cancelled) setProjectsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedLead?.id]);

  // Load attachable project docs (S3 uploads only — Drive links cannot be MIME-attached)
  useEffect(() => {
    setProjectDocs([]);
    if (!user?.id || !selectedProject?.id) {
      // Keep draft attachments while the lead's project list is still hydrating
      if (!projectsLoading && !desiredProjectIdRef.current) {
        setSelectedAttachmentIds([]);
      }
      setProjectDocsLoading(false);
      return;
    }

    let cancelled = false;
    setProjectDocsLoading(true);
    void (async () => {
      try {
        const { data, error } = await supabaseData
          .from('files')
          .select('id, file_name, mime_type, size_bytes, source, object_key')
          .eq('owner_user_id', user.id)
          .eq('entity_type', 'projects')
          .eq('entity_id', selectedProject.id)
          .eq('status', 'active')
          .order('created_at', { ascending: false });

        if (cancelled) return;
        if (error) {
          console.error('Failed to load project docs for attach:', error);
          setProjectDocs([]);
          return;
        }

        const attachable = (data || []).filter(
          (row) => row.source !== 'drive' && row.object_key
        ) as AttachableProjectDoc[];
        setProjectDocs(attachable);
        // Drop ids that no longer exist on this project (keep valid draft selections)
        setSelectedAttachmentIds((prev) =>
          prev.filter((id) => attachable.some((doc) => doc.id === id))
        );
      } catch (err) {
        if (!cancelled) {
          console.error('Exception loading project docs for attach:', err);
          setProjectDocs([]);
        }
      } finally {
        if (!cancelled) setProjectDocsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, selectedProject?.id, projectsLoading]);

  // Persist compose draft across tab changes / reloads
  useEffect(() => {
    if (!user?.id || !composeDraftReadyRef.current) return;
    if (skipComposeDraftPersistRef.current) {
      skipComposeDraftPersistRef.current = false;
      return;
    }
    saveMessagesComposeDraft(user.id, {
      subject: composeSourceRef.current.subject || messageSubject,
      content: composeSourceRef.current.content || messageContent,
      leadId: selectedLead?.id ?? null,
      // Keep desired project while the lead's project list is still loading (avoid wiping draft)
      projectId:
        selectedProject?.id ??
        (projectsLoading ? desiredProjectIdRef.current : null) ??
        null,
      attachmentIds: selectedAttachmentIds,
      activeTemplateId,
      updatedAt: Date.now(),
    });
  }, [
    user?.id,
    messageSubject,
    messageContent,
    selectedLead?.id,
    selectedProject?.id,
    selectedAttachmentIds,
    activeTemplateId,
    projectsLoading,
  ]);

  // Restore AI rewrite review / undo in memory (survives in-app navigation)
  useEffect(() => {
    if (!user?.id) {
      aiSessionReadyRef.current = false;
      return;
    }

    aiSessionReadyRef.current = false;
    const ai = loadMessagesAiSession(user.id);
    setRewriteSuggestion(ai?.rewriteSuggestion ?? null);
    setRewriteInstruction(ai?.rewriteInstruction ?? '');
    lastBeforeRewriteRef.current = ai?.lastBeforeRewrite ?? null;
    setHasPreviousForRewrite(Boolean(ai?.lastBeforeRewrite));
    skipAiSessionPersistRef.current = true;
    aiSessionReadyRef.current = true;
  }, [user?.id]);

  // Persist AI rewrite session in memory (per user)
  useEffect(() => {
    if (!user?.id || !aiSessionReadyRef.current) return;
    if (skipAiSessionPersistRef.current) {
      skipAiSessionPersistRef.current = false;
      return;
    }
    saveMessagesAiSession(user.id, {
      rewriteSuggestion,
      lastBeforeRewrite: lastBeforeRewriteRef.current,
      rewriteInstruction,
    });
  }, [user?.id, rewriteSuggestion, hasPreviousForRewrite, rewriteInstruction]);

  // Load profile for template variables (my_name / my_company)
  useEffect(() => {
    const fallbackName =
      (user as any)?.user_metadata?.name ||
      user?.email?.split('@')?.[0] ||
      '';
    const fallbackCompany = (user as any)?.user_metadata?.company || '';

    const loadProfile = async () => {
      try {
        const profile = await profilesService.getProfile();
        setMyName((profile?.name as string) || fallbackName);
        setMyCompany((profile?.company as string) || fallbackCompany);
        setEmailSignature(
          typeof profile?.email_signature === 'string' ? profile.email_signature : ''
        );
        const rawHtml =
          typeof profile?.email_signature_html === 'string' ? profile.email_signature_html : '';
        setEmailSignatureHtml(rawHtml ? await prepareEmailSignatureHtml(rawHtml) : '');
      } catch {
        setMyName(fallbackName);
        setMyCompany(fallbackCompany);
        setEmailSignature('');
        setEmailSignatureHtml('');
      }
    };

    if (user) loadProfile();
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    googleGmailService
      .getStatus()
      .then((status) => {
        if (!cancelled) setGmailStatus(status);
      })
      .catch(() => {
        if (!cancelled) setGmailStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);


  // Lead filtering

  // Personalize message with lead / optional project data
  const personalizeMessage = (content: string, lead: Lead | null = selectedLead) => {
    return personalizeMessageText(content, {
      lead,
      project: selectedProject,
      myName,
      myCompany,
      sectorLabel: lead ? getSectorLabel(lead.sector_id) : '',
      language,
    });
  };

  const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const rehydrateSourceFromDisplayed = (displayedText: string, previousSource: string) => {
    let source = normalizeFriendlyVariableTokens(displayedText);
    if (!selectedLead || !previousSource || !hasVariableToken(previousSource)) {
      return source;
    }

    for (const { token } of MESSAGE_VARIABLES) {
      if (!previousSource.includes(token)) continue;
      const resolved = personalizeMessage(token, selectedLead).trim();
      if (!resolved || resolved === token) continue;
      source = source.replace(new RegExp(escapeRegExp(resolved), 'g'), token);
    }

    return source;
  };

  const protectVariableTokens = (text: string, tokenMap: Map<string, string>) => {
    if (!text) return text;
    return text.replace(/\{[^{}\n]+\}/g, (token) => {
      const placeholder = `__LM_VAR_${tokenMap.size}__`;
      tokenMap.set(placeholder, token);
      return placeholder;
    });
  };

  const restoreVariableTokens = (text: string, tokenMap: Map<string, string>) => {
    if (!text || tokenMap.size === 0) return text;
    let restored = text;
    tokenMap.forEach((token, placeholder) => {
      restored = restored.replace(new RegExp(escapeRegExp(placeholder), 'g'), token);
    });
    return restored;
  };

  /** If AI resolved tokens using lead context, map resolved values back to source tokens. */
  const ensureSourceTokensPreserved = (
    originalSource: string,
    rewrittenSource: string,
    lead: Lead | null = selectedLead
  ) => {
    if (!originalSource || !rewrittenSource) return rewrittenSource;
    let out = rewrittenSource;

    for (const { token } of MESSAGE_VARIABLES) {
      if (!originalSource.includes(token) || out.includes(token)) continue;
      const resolved = personalizeMessage(token, lead).trim();
      if (!resolved || resolved === token) continue;
      out = out.replace(new RegExp(escapeRegExp(resolved), 'g'), token);
    }

    return out;
  };

  const insertComposeVariable = (token: string) => {
    const resolved = personalizeMessage(token, selectedLead);
    const insertion = resolved && resolved.trim() ? resolved : token;

    if (composeActiveField === 'subject') {
      const sourceBase = composeSourceRef.current.subject || messageSubject;
      const el = subjectInputRef.current;
      const start = el?.selectionStart ?? sourceBase.length;
      const end = el?.selectionEnd ?? sourceBase.length;
      composeSourceRef.current.subject = sourceBase.slice(0, start) + token + sourceBase.slice(end);
      insertTextAtCursor(subjectInputRef.current, insertion, messageSubject, setMessageSubject);
    } else {
      const sourceBase = composeSourceRef.current.content || messageContent;
      const el = contentInputRef.current;
      const start = el?.selectionStart ?? sourceBase.length;
      const end = el?.selectionEnd ?? sourceBase.length;
      composeSourceRef.current.content = sourceBase.slice(0, start) + token + sourceBase.slice(end);
      insertTextAtCursor(contentInputRef.current, insertion, messageContent, setMessageContent);
    }
  };

  // Get contact category to determine type
  const getContactCategory = (lead: Lead) => {
    if (!lead.contact_type_id) return null;
    return categories.find(cat => cat.id === lead.contact_type_id) || null;
  };

  // Check if contact type is email
  const isEmailContact = (lead: Lead): boolean => {
    const category = getContactCategory(lead);
    if (!category) return true;
    return isEmailCategory(category);
  };

  const getLeadChannel = (lead: Lead) => {
    const category = getContactCategory(lead);
    // Match isEmailContact default when type is unknown
    if (!category) return 'email' as const;
    return resolveContactChannel(category);
  };

  const getComposeSource = () => ({
    subject: composeSourceRef.current.subject || messageSubject,
    content: composeSourceRef.current.content || messageContent,
  });

  const clearActiveTemplate = () => {
    setActiveTemplateId(null);
    appliedTemplateRef.current = null;
  };

  const syncActiveTemplateFromSource = () => {
    if (!activeTemplateId) return;
    const template = templates.find((item) => item.id === activeTemplateId);
    if (!template) {
      clearActiveTemplate();
      return;
    }
    const expectedSubject = normalizeFriendlyVariableTokens(template.subject || '');
    const expectedContent = normalizeFriendlyVariableTokens(template.content || '');
    const { subject, content } = getComposeSource();
    if (subject !== expectedSubject || content !== expectedContent) {
      clearActiveTemplate();
    }
  };

  const getPersonalizedPayload = (lead: Lead | null = selectedLead) => {
    const { subject, content } = getComposeSource();
    if (!lead) {
      return {
        isEmail: false,
        subject: personalizeMessage(subject, null),
        body: personalizeMessage(content, null),
      };
    }
    const isEmail = isEmailContact(lead);
    return {
      isEmail,
      subject: isEmail ? personalizeMessage(subject, lead) : '',
      body: personalizeMessage(content, lead),
    };
  };

  const handleCopyMessage = async () => {
    const { content } = getComposeSource();
    if (!content.trim()) return;

    const { isEmail, subject: personalizedSubject, body } = getPersonalizedPayload(selectedLead);
    const textToCopy =
      isEmail && personalizedSubject.trim()
        ? `${t('messages.subject')}: ${personalizedSubject.trim()}\n\n${body}`
        : body;

    try {
      await navigator.clipboard.writeText(textToCopy);
      showToast(t('messages.messageCopied'), 'success');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      showToast(t('messages.copyError') || 'Erreur lors de la copie', 'error');
    }
  };

  const handleOpenContact = async () => {
    const { content } = getComposeSource();
    if (!selectedLead || !content.trim()) return;

    const channel = getLeadChannel(selectedLead);
    const contactValue = effectiveRecipient || selectedLead.contact_value || '';
    if (!contactValue) return;

    const { subject, body } = getPersonalizedPayload(selectedLead);

    const url = buildContactOpenUrl(channel, contactValue, { subject, body });
    if (!url) {
      showToast(t('messages.openContactUnavailable'), 'error');
      return;
    }

    const prefills = contactOpenPrefillsMessage(channel);
    if (!prefills) {
      try {
        await navigator.clipboard.writeText(body);
      } catch (error) {
        console.error('Failed to copy before open:', error);
        showToast(t('messages.copyError') || 'Erreur lors de la copie', 'error');
        return;
      }
    }

    window.open(url, '_blank', 'noopener,noreferrer');
    showToast(
      prefills ? t('messages.openContactOpened') : t('messages.messageCopiedAndOpened'),
      'success'
    );
  };

  const openGmailSendModal = () => {
    const { content } = getComposeSource();
    if (!selectedLead || !content.trim()) return;
    if (blockDemoWrite()) return;
    if (!gmailStatus?.connected) {
      navigate('/app/settings?tab=integrations&gmail=connect');
      return;
    }
    const to = composeRecipient.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      showToast(t('messages.gmailInvalidEmail'), 'error');
      return;
    }
    setShowGmailSendModal(true);
  };

  const handleConfirmGmailSend = async (payload: {
    to: string;
    subject: string;
    includeSignature: boolean;
  }) => {
    if (!selectedLead || gmailSendLoading) return;
    if (blockDemoWrite()) return;

    const { body } = getPersonalizedPayload(selectedLead);
    const signatureHtml = emailSignatureHtml
      ? await prepareEmailSignatureHtml(emailSignatureHtml)
      : '';
    const { bodyText, bodyHtml } = buildEmailBodies({
      body,
      signatureText: emailSignature,
      signatureHtml,
      includeSignature: payload.includeSignature,
    });

    // Close immediately; keep compose / recipient / draft intact
    setShowGmailSendModal(false);
    setComposeRecipient(payload.to);
    setGmailSendLoading(true);
    try {
      const res = await googleGmailService.send({
        leadId: selectedLead.id,
        to: payload.to,
        subject: payload.subject,
        body: bodyText,
        ...(bodyHtml ? { bodyHtml } : {}),
        threadId: linkedEmailThread?.gmailThreadId,
        ...(selectedAttachmentIds.length > 0 ? { fileIds: selectedAttachmentIds } : {}),
      });
      if (res.thread) setLinkedEmailThread(res.thread);
      setSelectedAttachmentIds([]);
      showToast(t('messages.gmailSent'), 'success');
    } catch (err: any) {
      const key = gmailErrorMessageKey(err);
      showToast(key ? t(key) : t('messages.gmailSendError'), 'error');
    } finally {
      setGmailSendLoading(false);
    }
  };

  const openChannel = selectedLead ? getLeadChannel(selectedLead) : null;
  const effectiveRecipient = composeRecipient.trim();
  const defaultRecipient = selectedLead?.contact_value || '';
  const recipientForOpen = effectiveRecipient || defaultRecipient;
  const composeSource = getComposeSource();
  const openPayload = selectedLead
    ? getPersonalizedPayload(selectedLead)
    : {
        isEmail: openChannel === 'email',
        subject:
          openChannel === 'email' ? personalizeMessage(composeSource.subject, null) : '',
        body: personalizeMessage(composeSource.content, null),
      };
  const openUrl =
    recipientForOpen && openChannel
      ? buildContactOpenUrl(openChannel, recipientForOpen, {
          subject: openChannel === 'email' ? openPayload.subject : '',
          body: openPayload.body,
        })
      : null;
  const openLabelKey = openChannel ? contactOpenLabelKey(openChannel) : null;
  const canOpenContact = Boolean(openUrl && openLabelKey);
  const composeReady = Boolean(composeSource.content.trim());

  const gmailSendReady =
    Boolean(selectedLead && composeSource.content.trim() && gmailStatus?.connected) &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(effectiveRecipient);

  const recipientPlaceholder =
    defaultRecipient ||
    (selectedLead && isEmailContact(selectedLead)
      ? 'contact@marque.com'
      : t('messages.recipientPlaceholder'));

  useEffect(() => {
    if (!selectedLead) {
      setComposeRecipient('');
      return;
    }
    setComposeRecipient(selectedLead.contact_value || '');
  }, [selectedLead?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const sourceSubject = composeSourceRef.current.subject;
    const sourceContent = composeSourceRef.current.content;

    if (sourceSubject && hasVariableToken(sourceSubject)) {
      setMessageSubject(selectedLead ? personalizeMessage(sourceSubject, selectedLead) : sourceSubject);
    }
    if (sourceContent && hasVariableToken(sourceContent)) {
      setMessageContent(selectedLead ? personalizeMessage(sourceContent, selectedLead) : sourceContent);
    }
  }, [selectedLead?.id, selectedProject?.id, myName, myCompany, language]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSplitLayout) {
      resetTextareaHeight(contentInputRef.current);
      resetTextareaHeight(rewriteInputRef.current);
      return;
    }
    syncAutoGrowTextarea(contentInputRef.current, MESSAGE_TEXTAREA_MIN_PX);
    syncAutoGrowTextarea(rewriteInputRef.current, REWRITE_TEXTAREA_MIN_PX);
  }, [isSplitLayout, messageContent, rewriteInstruction]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenTemplateModal = (template?: EmailTemplate) => {
    setEditingTemplate(template || null);
    setIsTemplateModalOpen(true);
  };

  const handleApplyTemplate = (template: EmailTemplate) => {
    if (activeTemplateId === template.id) {
      clearActiveTemplate();
      composeSourceRef.current = { subject: '', content: '' };
      setMessageSubject('');
      setMessageContent('');
      setRewriteSuggestion(null);
      clearRewriteUndo();
      setRewriteInstruction('');
      setIsTemplatesSheetOpen(false);
      return;
    }

    const rawSubject = normalizeFriendlyVariableTokens(template.subject || '');
    const rawContent = normalizeFriendlyVariableTokens(template.content || '');
    const nextSubject = selectedLead ? personalizeMessage(rawSubject, selectedLead) : rawSubject;
    const nextContent = selectedLead ? personalizeMessage(rawContent, selectedLead) : rawContent;

    composeSourceRef.current.subject = rawSubject;
    composeSourceRef.current.content = rawContent;
    setMessageSubject(nextSubject);
    setMessageContent(nextContent);
    setActiveTemplateId(template.id);
    appliedTemplateRef.current = { id: template.id, subject: nextSubject, content: nextContent };
    setRewriteSuggestion(null);
    clearRewriteUndo();
    setRewriteInstruction('');
    setIsTemplatesSheetOpen(false);
  };

  const clearRewriteUndo = () => {
    lastBeforeRewriteRef.current = null;
    setHasPreviousForRewrite(false);
  };

  const handleRewriteWithAI = async (instructionOverride?: string, displayLabel?: string) => {
    if (rewriteLoading || !messageContent.trim()) return;
    if (!userPlan?.has_ai_rewrite) {
      setShowProRewriteModal(true);
      return;
    }
    const instruction = (instructionOverride ?? rewriteInstruction).trim() || undefined;
    const actionLabel =
      (displayLabel ?? rewriteInstruction).trim() || t('messages.rewriteWithAI');
    // Send source text when available so AI can preserve variable tokens.
    const sourceSubject = normalizeFriendlyVariableTokens(
      composeSourceRef.current.subject || messageSubject
    );
    const sourceContent = normalizeFriendlyVariableTokens(
      composeSourceRef.current.content || messageContent
    );
    const tokenMap = new Map<string, string>();
    const protectedSubject = protectVariableTokens(sourceSubject, tokenMap);
    const protectedContent = protectVariableTokens(sourceContent, tokenMap);
    const sourceHasVariables =
      hasVariableToken(sourceSubject) || hasVariableToken(sourceContent) || tokenMap.size > 0;

    setRewriteLoading(true);
    try {
      const res = await aiRewriteService.rewrite({
        content: protectedContent,
        instruction,
        lead: selectedLead
          ? {
              name: selectedLead.name,
              contactValue: selectedLead.contact_value,
              sector: getSectorLabel(selectedLead.sector_id),
            }
          : undefined,
        preserveVariableTokens: sourceHasVariables,
        subject: protectedSubject || undefined,
      });
      const decodedSubject = ensureSourceTokensPreserved(
        sourceSubject,
        restoreVariableTokens(res.subject, tokenMap)
      );
      const decodedContent = ensureSourceTokensPreserved(
        sourceContent,
        restoreVariableTokens(res.content, tokenMap)
      );
      const nextSourceSubject = normalizeFriendlyVariableTokens(decodedSubject);
      const nextSourceContent = normalizeFriendlyVariableTokens(decodedContent);
      const displaySubject = selectedLead
        ? personalizeMessage(nextSourceSubject, selectedLead)
        : nextSourceSubject;
      const displayContent = selectedLead
        ? personalizeMessage(nextSourceContent, selectedLead)
        : nextSourceContent;

      if (displaySubject === messageSubject && displayContent === messageContent) {
        showToast(t('messages.rewriteNoChanges'), 'info');
        return;
      }

      setRewriteSuggestion({
        actionLabel,
        subjectHunks: buildHunksFromTexts(messageSubject, displaySubject, 'subj'),
        contentHunks: buildHunksFromTexts(messageContent, displayContent, 'body'),
        suggestedSourceSubject: nextSourceSubject,
        suggestedSourceContent: nextSourceContent,
      });
    } catch (err: any) {
      const code = err?.code as string | undefined;
      const messageKey = code ? REWRITE_ERROR_KEYS[code] : null;
      const translated = messageKey ? t(messageKey) : null;
      showToast(translated || err?.message || t('messages.rewriteFailed'), 'error');
    } finally {
      setRewriteLoading(false);
    }
  };

  const handleQuickRewrite = (instruction: string, displayLabel?: string) => {
    setRewriteInstruction(displayLabel ?? instruction);
    void handleRewriteWithAI(instruction, displayLabel);
  };

  const commitRewriteFromHunks = (
    suggestion: NonNullable<typeof rewriteSuggestion>,
    subjectHunks: DiffHunk[],
    contentHunks: DiffHunk[]
  ) => {
    const changeHunks = [...subjectHunks, ...contentHunks].filter(
      (h): h is Extract<DiffHunk, { kind: 'change' }> => h.kind === 'change'
    );
    if (changeHunks.length === 0 || changeHunks.every((h) => h.decision === 'rejected')) {
      setRewriteSuggestion(null);
      return;
    }

    lastBeforeRewriteRef.current = {
      subject: messageSubject,
      content: messageContent,
      sourceSubject: composeSourceRef.current.subject || messageSubject,
      sourceContent: composeSourceRef.current.content || messageContent,
    };

    const allAccepted = changeHunks.every((h) => h.decision === 'accepted');
    let nextSourceSubject: string;
    let nextSourceContent: string;
    let nextSubjectDisplay: string;
    let nextContentDisplay: string;

    if (allAccepted) {
      nextSourceSubject = suggestion.suggestedSourceSubject;
      nextSourceContent = suggestion.suggestedSourceContent;
      nextSubjectDisplay = selectedLead
        ? personalizeMessage(nextSourceSubject, selectedLead)
        : nextSourceSubject;
      nextContentDisplay = selectedLead
        ? personalizeMessage(nextSourceContent, selectedLead)
        : nextSourceContent;
    } else {
      nextSubjectDisplay = resolveHunksText(subjectHunks);
      nextContentDisplay = resolveHunksText(contentHunks);
      nextSourceSubject = rehydrateSourceFromDisplayed(
        nextSubjectDisplay,
        composeSourceRef.current.subject || messageSubject
      );
      nextSourceContent = rehydrateSourceFromDisplayed(
        nextContentDisplay,
        composeSourceRef.current.content || messageContent
      );
    }

    composeSourceRef.current = {
      subject: nextSourceSubject,
      content: nextSourceContent,
    };
    setMessageSubject(nextSubjectDisplay);
    setMessageContent(nextContentDisplay);
    setRewriteSuggestion(null);
    clearActiveTemplate();
    setHasPreviousForRewrite(true);
  };

  const handleRewriteHunkDecide = (
    field: 'subject' | 'content',
    hunkId: string,
    decision: 'accepted' | 'rejected'
  ) => {
    setRewriteSuggestion((prev) => {
      if (!prev) return prev;
      const subjectHunks =
        field === 'subject'
          ? prev.subjectHunks.map((h) =>
              h.kind === 'change' && h.id === hunkId ? { ...h, decision } : h
            )
          : prev.subjectHunks;
      const contentHunks =
        field === 'content'
          ? prev.contentHunks.map((h) =>
              h.kind === 'change' && h.id === hunkId ? { ...h, decision } : h
            )
          : prev.contentHunks;
      const next = { ...prev, subjectHunks, contentHunks };
      const pending =
        countPendingHunks(subjectHunks) + countPendingHunks(contentHunks);
      if (pending === 0) {
        // Defer commit so state update for suggestion clear happens cleanly
        queueMicrotask(() => commitRewriteFromHunks(next, subjectHunks, contentHunks));
      }
      return pending === 0 ? null : next;
    });
  };

  const handleAcceptAllRewriteHunks = () => {
    if (!rewriteSuggestion) return;
    const subjectHunks = decideAllHunks(rewriteSuggestion.subjectHunks, 'accepted');
    const contentHunks = decideAllHunks(rewriteSuggestion.contentHunks, 'accepted');
    commitRewriteFromHunks(rewriteSuggestion, subjectHunks, contentHunks);
  };

  const handleRejectAllRewriteHunks = () => {
    setRewriteSuggestion(null);
  };

  const handleRevertRewrite = () => {
    const prev = lastBeforeRewriteRef.current;
    if (!prev) return;
    composeSourceRef.current = {
      subject: prev.sourceSubject,
      content: prev.sourceContent,
    };
    setMessageSubject(prev.subject);
    setMessageContent(prev.content);
    clearRewriteUndo();
    setRewriteSuggestion(null);
  };

  const handleCloseTemplateModal = () => {
    setIsTemplateModalOpen(false);
    setEditingTemplate(null);
  };

  const handleSaveTemplate = (templateData: Omit<EmailTemplate, 'id'>) => {
    if (editingTemplate) {
      // Update with optimistic UI (instant feedback)
      updateTemplate({ ...editingTemplate, ...templateData }, { previousItem: editingTemplate });

      // If user is currently composing from this template, refresh the compose fields.
      // To avoid clobbering user edits, only auto-apply if the user hasn't modified the fields since applying.
      const applied = appliedTemplateRef.current;
      if (applied && applied.id === editingTemplate.id) {
        const subjectUnchanged = messageSubject === applied.subject;
        const contentUnchanged = messageContent === applied.content;
        if (subjectUnchanged && contentUnchanged) {
          appliedTemplateRef.current = { id: applied.id, subject: templateData.subject, content: templateData.content };
          setMessageSubject(templateData.subject);
          setMessageContent(templateData.content);
        }
      }
    } else {
      // Create with optimistic UI (instant feedback)
      addTemplate(templateData);
    }
    handleCloseTemplateModal();
  };

  const handleCopyTemplate = (template: EmailTemplate) => {
    // Copy with optimistic UI (instant feedback)
    addTemplate({
      name: `${template.name} (${t('messages.copy')})`,
      type: template.type,
      subject: template.subject,
      content: template.content,
    });
    showToast(t('messages.copied', { name: template.name }), 'success');
  };

  const handleDeleteTemplate = (id: string) => {
    const templateToDelete = templates.find(t => t.id === id);
    if (!templateToDelete) return;

    // Store the template data for undo
    const templateData = { ...templateToDelete };
    
    // Delete with optimistic UI (instant feedback)
    deleteTemplate(id);
    if (activeTemplateId === id) {
      clearActiveTemplate();
    }

    // Show toast with undo immediately
    showToastWithUndo(
      t('messages.deleted', { name: templateToDelete.name }),
      async () => {
        // Undo: recreate the template via service, then patch locally (no global refresh)
        const created = await templatesService.create({
          name: templateData.name,
          type: templateData.type,
          subject: templateData.subject,
          content: templateData.content,
        });
        if (created) {
          insertTemplate(created);
        }
      },
      'error'
    );
  };

  const handleExportTemplates = async () => {
    try {
      if (templates.length === 0) {
        showToast(t('messages.noTemplatesToExport'), 'warning');
        return;
      }

      const exportHeaders = [
        t('templates.name'),
        t('templates.subject'),
        t('templates.contentType'),
        t('templates.content'),
        t('templates.createdAt')
      ];

      const exportData = templates.map(template => ({
        [t('templates.name')]: template.name,
        [t('templates.subject')]: template.subject || '',
        [t('templates.contentType')]: template.type,
        [t('templates.content')]: template.content || '',
        [t('templates.createdAt')]: template.createdAt != null ? formatDateTimeForExport(template.createdAt) : ''
      }));

      exportToCSV(exportData, `templates-${new Date().toISOString().split('T')[0]}`, exportHeaders);
      showToast(t('common.exportSuccess', { count: exportData.length }) || `${exportData.length} templates exportés`, 'success');
    } catch (error) {
      console.error('Error exporting templates:', error);
      showToast(t('messages.exportError') || 'Erreur lors de l\'export des templates', 'error');
    }
  };

  // Progressive loading: keep shell visible; loaders are local to sections.

  const templatesPickerProps = {
    templates,
    filteredTemplates,
    loading,
    error,
    typeFilter,
    onTypeFilterChange: setTypeFilter,
    activeTemplateId,
    onApply: handleApplyTemplate,
    onEdit: (template: EmailTemplate) => {
      setIsTemplatesSheetOpen(false);
      handleOpenTemplateModal(template);
    },
    onDelete: handleDeleteTemplate,
    onCopy: handleCopyTemplate,
    onCreate: () => {
      setIsTemplatesSheetOpen(false);
      handleOpenTemplateModal();
    },
  };

  return (
    <div className={`w-full flex flex-col min-[900px]:flex-1 min-[900px]:min-h-0 min-[900px]:overflow-hidden ${PAGE_GAP}`}>
      {/* Header */}
      <PageHeader
        className="shrink-0"
        title={t('messages.title')}
        description={t('messages.manageTemplates')}
        actionsConfig={{
          newButtonKey: 'messages.newTemplate',
          onCreateNew: () => handleOpenTemplateModal(),
          onExport: handleExportTemplates
        }}
      />

      {/* Main Section — compose-first on mobile; templates rail from 900px */}
      <div className={`grid grid-cols-1 min-[900px]:flex-1 min-[900px]:min-h-0 min-[900px]:overflow-hidden min-[900px]:grid-cols-[minmax(260px,320px)_minmax(0,1fr)] ${PAGE_GRID_GAP} min-[900px]:items-stretch`}>
        {/* Message Templates — desktop rail only */}
        <Card className="hidden min-[900px]:flex h-full flex-col min-w-0 min-h-0 overflow-hidden">
          <CardHeader className="shrink-0">
            <CardTitle className="flex items-center space-x-2">
              <Mail className="w-5 h-5 text-gray-600" />
              <span>{t('messages.emailTemplates')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="min-w-0 flex flex-col flex-1 min-h-0 overflow-hidden">
            <TemplatesPicker {...templatesPickerProps} scrollable />
          </CardContent>
          <CardFooter className="shrink-0 mt-auto -mx-3 sm:-mx-4 xl:-mx-6 -mb-3 sm:-mb-4 xl:-mb-6 px-3 sm:px-4 xl:px-6 pb-3 sm:pb-4 xl:pb-4 pt-3 bg-gray-50/70">
            <VariableChips
              variant="compact"
              onInsert={insertComposeVariable}
              projectEnabled={Boolean(selectedProject)}
            />
          </CardFooter>
        </Card>

        {/* Compose Message */}
        <Card className="h-auto min-[900px]:h-full flex flex-col min-w-0 min-[900px]:min-h-0 min-[900px]:overflow-hidden">
          <CardHeader className="shrink-0">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="flex items-center space-x-2 min-w-0">
                <Grid3X3 className="w-5 h-5 text-gray-600 shrink-0" />
                <span className="truncate">{t('messages.composeMessage')}</span>
              </CardTitle>
              {!isSplitLayout && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsTemplatesSheetOpen(true)}
                  icon={<Mail className="w-4 h-4" />}
                  className="shrink-0"
                >
                  {t('messages.templatesShort')}
                  {activeTemplateId ? (
                    <span
                      className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-primary-500"
                      aria-hidden
                    />
                  ) : null}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="min-w-0 flex flex-col min-[900px]:flex-1 min-[900px]:min-h-0 min-[900px]:overflow-hidden">
            <div className={`flex flex-col min-[900px]:flex-1 min-[900px]:min-h-0 min-[900px]:overflow-hidden ${PAGE_GAP}`}>
              {/* Lead + optional project (project only when the lead has any) */}
              <div
                className={`grid gap-x-4 gap-y-2 items-end shrink-0 overflow-visible ${
                  selectedLead && (projectsLoading || leadProjects.length > 0)
                    ? 'grid-cols-1 sm:grid-cols-2'
                    : 'grid-cols-1'
                }`}
              >
                <div className="overflow-visible px-px">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('messages.selectBrand')}
                  </label>
                  <LeadSearch
                    leads={[]}
                    selectedLead={selectedLead}
                    onSelectLead={(lead) => {
                      if (lead?.id !== selectedLead?.id) {
                        desiredProjectIdRef.current = null;
                        setSelectedAttachmentIds([]);
                      }
                      setSelectedLead(lead);
                    }}
                    categories={categories}
                    placeholder={t('messages.searchBrand')}
                    searchFn={(term) => leadsService.search(term, 10)}
                    onCreateLead={requestCreateLead}
                  />
                </div>
                {selectedLead && (projectsLoading || leadProjects.length > 0) ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('messages.selectProjectOptional')}
                    </label>
                    <Select
                      value={selectedProject?.id || ''}
                      onChange={(value) => {
                        const next = leadProjects.find((p) => p.id === value) || null;
                        if (next?.id !== selectedProject?.id) {
                          setSelectedAttachmentIds([]);
                        }
                        desiredProjectIdRef.current = next?.id ?? null;
                        setSelectedProject(next);
                      }}
                      options={[
                        { value: '', label: t('messages.noProjectSelected') },
                        ...leadProjects.map((p) => ({
                          value: p.id,
                          label: p.name,
                        })),
                      ]}
                      placeholder={
                        projectsLoading
                          ? t('common.loading')
                          : t('messages.selectProjectOptional')
                      }
                      loading={projectsLoading}
                      disabled={projectsLoading || leadProjects.length === 0}
                      showEmptyOptionInList={false}
                    />
                  </div>
                ) : null}
              </div>

              {/* Compose mail */}
              <div className="flex flex-col min-[900px]:flex-1 min-[900px]:min-h-[12rem]">
                <div className="rounded-xl border border-gray-200/80 bg-white overflow-hidden flex flex-col min-[900px]:flex-1 min-[900px]:min-h-0">
                  <div
                    className={
                      isCompactComposeFields
                        ? 'shrink-0 border-b border-gray-200/80 px-4 py-2 flex items-center gap-2'
                        : 'shrink-0 border-b border-gray-200/80 px-4 py-3'
                    }
                  >
                    <label
                      className={
                        isCompactComposeFields
                          ? 'shrink-0 text-xs font-medium text-gray-500'
                          : 'block text-xs font-medium text-gray-500 mb-1.5'
                      }
                    >
                      {isCompactComposeFields ? `${t('messages.to')} :` : t('messages.to')}
                    </label>
                    <input
                      type="text"
                      autoComplete="off"
                      placeholder={recipientPlaceholder}
                      value={composeRecipient}
                      onChange={(e) => setComposeRecipient(e.target.value)}
                      className={`border-0 bg-transparent p-0 text-base sm:text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-0 ${
                        isCompactComposeFields ? 'min-w-0 flex-1' : 'w-full'
                      }`}
                    />
                  </div>
                  {(!selectedLead || isEmailContact(selectedLead)) && (
                    <div
                      className={
                        isCompactComposeFields &&
                        !(
                          rewriteSuggestion &&
                          rewriteSuggestion.subjectHunks.some((h) => h.kind === 'change')
                        )
                          ? 'shrink-0 border-b border-gray-200/80 px-4 py-2 flex items-center gap-2'
                          : 'shrink-0 border-b border-gray-200/80 px-4 py-3'
                      }
                    >
                      <label
                        className={
                          isCompactComposeFields &&
                          !(
                            rewriteSuggestion &&
                            rewriteSuggestion.subjectHunks.some((h) => h.kind === 'change')
                          )
                            ? 'shrink-0 text-xs font-medium text-gray-500'
                            : 'block text-xs font-medium text-gray-500 mb-1.5'
                        }
                      >
                        {isCompactComposeFields
                          ? `${t('messages.subject')} :`
                          : t('messages.subject')}
                      </label>
                      {rewriteSuggestion &&
                      rewriteSuggestion.subjectHunks.some((h) => h.kind === 'change') ? (
                        <InlineRewriteDiff
                          hunks={rewriteSuggestion.subjectHunks}
                          onDecide={(id, decision) =>
                            handleRewriteHunkDecide('subject', id, decision)
                          }
                          acceptLabel={t('messages.rewriteAccept')}
                          rejectLabel={t('messages.rewriteReject')}
                        />
                      ) : (
                        <input
                          ref={subjectInputRef}
                          type="text"
                          placeholder={t('messages.emailSubject')}
                          value={messageSubject}
                          onChange={(e) => {
                            const previousSource = composeSourceRef.current.subject || messageSubject;
                            const source = rehydrateSourceFromDisplayed(e.target.value, previousSource);
                            composeSourceRef.current.subject = source;
                            setMessageSubject(
                              selectedLead ? personalizeMessage(source, selectedLead) : source
                            );
                            syncActiveTemplateFromSource();
                          }}
                          onFocus={() => setComposeActiveField('subject')}
                          disabled={Boolean(rewriteSuggestion)}
                          className={`border-0 bg-transparent p-0 text-base sm:text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-0 disabled:opacity-70 ${
                            isCompactComposeFields ? 'min-w-0 flex-1' : 'w-full'
                          }`}
                        />
                      )}
                    </div>
                  )}
                  <div className="min-h-[8rem] flex flex-col min-[900px]:flex-1 min-[900px]:min-h-0 min-[900px]:overflow-hidden">
                    {rewriteSuggestion ? (
                      <div className="min-h-[8rem] flex flex-col min-[900px]:flex-1 min-[900px]:min-h-0">
                        <div className="px-4 py-3 min-[900px]:flex-1 min-[900px]:overflow-y-auto min-[900px]:overscroll-contain">
                          <InlineRewriteDiff
                            hunks={rewriteSuggestion.contentHunks}
                            onDecide={(id, decision) =>
                              handleRewriteHunkDecide('content', id, decision)
                            }
                            acceptLabel={t('messages.rewriteAccept')}
                            rejectLabel={t('messages.rewriteReject')}
                          />
                        </div>
                        <div className="shrink-0 px-4 mt-0 pt-2.5 pb-3 border-t border-dashed border-gray-200/90 flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={handleRejectAllRewriteHunks}
                              disabled={rewriteLoading}
                              className="rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors disabled:opacity-50"
                            >
                              {t('messages.rewriteRejectAll')}
                            </button>
                            <span className="text-gray-300 select-none" aria-hidden>
                              ·
                            </span>
                            <button
                              type="button"
                              onClick={handleAcceptAllRewriteHunks}
                              disabled={rewriteLoading}
                              className="rounded-md px-2 py-1 text-xs font-medium text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 transition-colors disabled:opacity-50"
                            >
                              {t('messages.rewriteAcceptAll')}
                            </button>
                          </div>
                          <p className="text-[11px] font-medium text-primary-700 text-right truncate max-w-full sm:max-w-[55%]">
                            {t('messages.rewriteInlineHint', {
                              action: rewriteSuggestion.actionLabel,
                            })}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <textarea
                        ref={contentInputRef}
                        aria-label={t('messages.message')}
                        placeholder={t('messages.personalizedMessage')}
                        value={messageContent}
                        onChange={(e) => {
                          const previousSource = composeSourceRef.current.content || messageContent;
                          const source = rehydrateSourceFromDisplayed(e.target.value, previousSource);
                          composeSourceRef.current.content = source;
                          setMessageContent(
                            selectedLead ? personalizeMessage(source, selectedLead) : source
                          );
                          syncActiveTemplateFromSource();
                        }}
                        onInput={syncMessageTextareaHeight}
                        onFocus={() => setComposeActiveField('content')}
                        rows={1}
                        className="w-full min-h-[8rem] resize-none overflow-hidden border-0 bg-transparent px-4 py-3 text-base sm:text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-0 min-[900px]:flex-1 min-[900px]:overflow-y-auto min-[900px]:overscroll-contain"
                      />
                    )}
                  </div>
                  <div className="shrink-0 border-t border-gray-200/80 bg-gray-50/50 px-3 py-2.5 flex flex-col gap-2 min-[480px]:flex-row min-[480px]:flex-wrap min-[480px]:items-end min-[480px]:justify-between">
                    <div className="min-w-0 w-full min-[480px]:flex-1 min-[480px]:min-w-0">
                      {!gmailStatus?.connected &&
                        selectedLead &&
                        isEmailContact(selectedLead) && (
                        <Link
                          to="/app/settings?tab=integrations&gmail=connect"
                          className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 rounded"
                        >
                          <Link2 className="w-4 h-4 shrink-0" aria-hidden />
                          {t('messages.gmailConnectHint')}
                        </Link>
                      )}
                      {gmailStatus?.connected && selectedProject && (
                        <ProjectAttachMenu
                          documents={projectDocs}
                          selectedIds={selectedAttachmentIds}
                          onChange={setSelectedAttachmentIds}
                          loading={projectDocsLoading}
                        />
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2 w-full min-[480px]:w-auto min-[480px]:justify-end min-[480px]:shrink-0">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleCopyMessage}
                        icon={<Copy className="w-4 h-4" />}
                        disabled={!composeReady}
                        className="flex-1 min-[480px]:flex-none"
                      >
                        {t('messages.copyToClipboard')}
                      </Button>
                      {gmailStatus?.connected && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={openGmailSendModal}
                          icon={<SendIcon className="w-4 h-4" />}
                          loading={gmailSendLoading}
                          disabled={!gmailSendReady || gmailSendLoading}
                          className="flex-1 min-[480px]:flex-none"
                        >
                          {t('messages.gmailSend')}
                        </Button>
                      )}
                      {canOpenContact &&
                        openLabelKey &&
                        !(gmailStatus?.connected && openChannel === 'email') && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleOpenContact}
                            icon={
                              openChannel === 'email' ? (
                                <SendIcon className="w-4 h-4" />
                              ) : (
                                <ExternalLink className="w-4 h-4" />
                              )
                            }
                            disabled={!composeReady}
                            className="flex-1 min-[480px]:flex-none"
                          >
                            {t(openLabelKey)}
                          </Button>
                        )}
                    </div>
                  </div>
                </div>
              </div>

              {!isSplitLayout && (
                <div className="shrink-0 rounded-xl border border-gray-200/80 bg-gray-50/70 px-3 py-2.5">
                  <VariableChips
                    variant="compact"
                    onInsert={insertComposeVariable}
                    projectEnabled={Boolean(selectedProject)}
                  />
                </div>
              )}

              <div className={`shrink-0 flex flex-col ${PAGE_GAP} min-[900px]:min-h-0 min-[900px]:max-h-60 min-[900px]:overflow-y-auto min-[900px]:overscroll-contain`}>
              {/* Réécriture IA — suggestions to accept/reject; Free → upgrade modal on use */}
              <div className="relative rounded-xl border border-gray-200/80 bg-gray-50/40 p-3.5">
                <div className="flex items-center justify-between gap-3 mb-2.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary-500/10 text-primary-600">
                      <Sparkles className="h-3.5 w-3.5" aria-hidden />
                    </span>
                    <span className="text-sm font-medium text-gray-800 truncate">
                      {t('messages.rewriteAIPromptLabel')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <ActionIconButton
                      icon={Undo2}
                      onClick={handleRevertRewrite}
                      variant="view"
                      size="sm"
                      tooltip={t('messages.rewriteBackTooltip')}
                      disabled={!hasPreviousForRewrite || rewriteLoading || Boolean(rewriteSuggestion)}
                    />
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => void handleRewriteWithAI()}
                      disabled={rewriteLoading || !messageContent.trim()}
                      icon={
                        rewriteLoading ? undefined : (
                          <Sparkles className="w-3.5 h-3.5 text-white" />
                        )
                      }
                      loading={rewriteLoading}
                      className="shrink-0 rounded-xl"
                    >
                      {rewriteLoading
                        ? t('messages.rewriteWithAILoading')
                        : t('messages.rewriteSuggest')}
                    </Button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-2.5">
                  {AI_QUICK_ACTION_KEYS.map((key) => {
                    const label = t(`messages.rewriteQuick.${key}`);
                    const instruction = t(`messages.rewriteQuickInstruction.${key}`);
                    const tooltip = t(`messages.rewriteQuickTooltip.${key}`);
                    return (
                      <Tooltip key={key} content={tooltip}>
                        <button
                          type="button"
                          disabled={rewriteLoading || !messageContent.trim()}
                          onClick={() => handleQuickRewrite(instruction, label)}
                          className="rounded-lg px-2.5 py-1 text-xs font-medium bg-white border border-gray-200 text-gray-700 hover:border-primary-300 hover:text-primary-700 hover:bg-primary-50/60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {label}
                        </button>
                      </Tooltip>
                    );
                  })}
                </div>

                <TextArea
                  ref={rewriteInputRef}
                  placeholder={t('messages.rewriteWithAIPrompt')}
                  value={rewriteInstruction}
                  onChange={(e) => setRewriteInstruction(e.target.value)}
                  disabled={rewriteLoading}
                  rows={2}
                  noFocusRing
                  onInput={syncRewriteTextareaHeight}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void handleRewriteWithAI();
                    }
                  }}
                  className="resize-none overflow-hidden bg-white/80 min-[900px]:max-h-[4.5rem] min-[900px]:overflow-y-auto min-[900px]:overscroll-contain"
                />
              </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Modal
        isOpen={!isSplitLayout && isTemplatesSheetOpen}
        onClose={() => setIsTemplatesSheetOpen(false)}
        title={t('messages.emailTemplates')}
        size="lg"
      >
        <TemplatesPicker {...templatesPickerProps} />
      </Modal>

      {/* Template Modal */}
      <TemplateModal
        isOpen={isTemplateModalOpen}
        onClose={handleCloseTemplateModal}
        onSave={handleSaveTemplate}
        template={editingTemplate}
      />

      {leadCreateModal}

      <GmailSendModal
        isOpen={showGmailSendModal}
        onClose={() => setShowGmailSendModal(false)}
        onConfirm={handleConfirmGmailSend}
        loading={gmailSendLoading}
        initialTo={composeRecipient.trim()}
        initialSubject={
          selectedLead
            ? getPersonalizedPayload(selectedLead).subject.trim() ||
              linkedEmailThread?.subject?.trim() ||
              ''
            : ''
        }
        bodyPreview={
          selectedLead ? getPersonalizedPayload(selectedLead).body : messageContent
        }
        emailSignature={emailSignature}
        emailSignatureHtml={emailSignatureHtml}
        leadName={selectedLead?.name}
        fromEmail={gmailStatus?.email}
        attachments={projectDocs.filter((doc) => selectedAttachmentIds.includes(doc.id))}
      />

      <UpgradePromptModal
        isOpen={showProRewriteModal}
        onClose={() => setShowProRewriteModal(false)}
        title={t('messages.proFeatureModalTitle')}
        message={t('messages.rewriteWithAIUpgradeTitle')}
        onUpgrade={() => {
          setShowProRewriteModal(false);
          navigateToPricing(navigate);
        }}
      />

    </div>
  );
};

export default Messages;
